/**
 * ScheduleVisualizer
 * -------------------
 * Provides a city/date filtered matrix that shows every rage room as a column
 * and each 30-minute time slot as a row. Capacity dots inside each cell
 * represent individual smashers (booked vs. open) so operators can gauge
 * utilization at a glance.
 */
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useApiBase } from 'm34n-ui';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import InputLabel from '@mui/material/InputLabel';
import LinearProgress from '@mui/material/LinearProgress';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';

import useApi from '../../hooks/useApi';
import FiltersBar from './components/FiltersBar';
import ScheduleLegend from './components/ScheduleLegend';
import ScheduleMatrix from './components/ScheduleMatrix';
import AddSlotModal from './components/AddSlotModal';
import EditSlotModal from './components/EditSlotModal';
import { buildCsvContent, downloadCsvFile } from './utils/csvExportUtils';
import { getEffectiveRemainingCapacity } from './utils/capacityUtils';

const MINUTES_PER_SLOT = 30;
const DEFAULT_START_HOUR = 8;
const DEFAULT_END_HOUR = 22;
const EVENT_FOCUS_MONTH = 12;
const EVENT_FOCUS_DAY = 11;
const DEFAULT_DATE = buildDefaultScheduleDate();
const FALLBACK_TIME_SLOTS = buildFallbackTimeSlots();
const DEFAULT_ROWS_PER_PAGE = 24;
const ROWS_PER_PAGE_OPTIONS = [8, 12, 16, 24];
const SCHEDULE_CSV_COLUMNS = [
  { key: 'city', label: 'City' },
  { key: 'date', label: 'Date' },
  { key: 'roomName', label: 'Room' },
  { key: 'roomStatus', label: 'Room status' },
  { key: 'timeSlot', label: 'Time slot (24h)' },
  { key: 'timeSlotDisplay', label: 'Time slot (display)' },
  { key: 'sessionLabels', label: 'Session labels' },
  { key: 'totalCapacity', label: 'Total capacity' },
  { key: 'bookedCount', label: 'Booked count' },
  { key: 'remainingCapacity', label: 'Remaining capacity' },
  { key: 'isBlocked', label: 'Blocked' },
  { key: 'slotCount', label: 'Configured slots' },
  { key: 'slotLabels', label: 'Slot labels' },
  { key: 'bookingCount', label: 'Booking count' },
  { key: 'bookingCustomerNames', label: 'Booking customers' },
  { key: 'bookingCustomerEmails', label: 'Booking emails' },
  { key: 'bookingCustomerPhones', label: 'Booking phones' },
  { key: 'bookingStatuses', label: 'Booking statuses' },
  { key: 'notes', label: 'Notes' },
];
const BOOKING_CSV_COLUMNS = [
  { key: 'bookingId', label: 'Booking ID' },
  { key: 'date', label: 'Date' },
  { key: 'city', label: 'City' },
  { key: 'roomName', label: 'Room' },
  { key: 'sessionLabel', label: 'Session' },
  { key: 'timeSlot', label: 'Time slot (24h)' },
  { key: 'timeSlotDisplay', label: 'Time slot (display)' },
  { key: 'customerName', label: 'Customer name' },
  { key: 'customerEmail', label: 'Customer email' },
  { key: 'customerPhone', label: 'Customer phone' },
  { key: 'partySize', label: 'Party size' },
  { key: 'slotLabels', label: 'Slot labels' },
  { key: 'status', label: 'Status' },
  { key: 'specialNote', label: 'Special note' },
];

const slugifyForFilename = (value = '') =>
  value
    .toString()
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '') || 'all';

const sanitizeDateForFilename = (value = '') => value.replace(/[^0-9-]/g, '') || 'date';

function buildDefaultScheduleDate() {
  const now = new Date();
  const target = new Date(2025, EVENT_FOCUS_MONTH - 1, EVENT_FOCUS_DAY);
  const month = String(target.getMonth() + 1).padStart(2, '0');
  const day = String(target.getDate()).padStart(2, '0');
  return `${target.getFullYear()}-${month}-${day}`;
}

function buildFallbackTimeSlots() {
  const slots = [];
  for (let minutes = DEFAULT_START_HOUR * 60; minutes <= DEFAULT_END_HOUR * 60; minutes += MINUTES_PER_SLOT) {
    const hours = String(Math.floor(minutes / 60)).padStart(2, '0');
    const mins = String(minutes % 60).padStart(2, '0');
    slots.push(`${hours}:${mins}`);
  }
  return slots;
}

const unwrapApiRows = (response) => {
  if (!response) {
    return [];
  }
  if (Array.isArray(response)) {
    return response;
  }
  if (Array.isArray(response.rows)) {
    return response.rows;
  }
  if (Array.isArray(response.data?.rows)) {
    return response.data.rows;
  }
  if (Array.isArray(response.rooms)) {
    return response.rooms;
  }
  if (Array.isArray(response.data?.rooms)) {
    return response.data.rooms;
  }
  if (Array.isArray(response.bookings)) {
    return response.bookings;
  }
  if (Array.isArray(response.data?.bookings)) {
    return response.data.bookings;
  }
  if (Array.isArray(response.data)) {
    return response.data;
  }
  if (Array.isArray(response.items)) {
    return response.items;
  }
  return [];
};

const resolveRoomIdFromRoomData = (room = {}) =>
  room.id || room._id || room.roomId || room.slug || room.name || null;

const resolveRoomIdFromRow = (row = {}) =>
  row.roomId ||
  row.room?._id ||
  row.room?.id ||
  row.room?.roomId ||
  row.room?.slug ||
  null;

const resolveRoomIdFromBooking = (booking = {}) =>
  booking.roomId ||
  booking.room?._id ||
  booking.room?.id ||
  booking.room?.roomId ||
  null;

const normalizeTimeSlotValue = (value) => {
  if (!value) {
    return null;
  }
  const [hours = '', minutes = ''] = value.split(':');
  const normalizedHours = hours.padStart(2, '0');
  const normalizedMinutes = minutes.slice(0, 2).padStart(2, '0');
  return `${normalizedHours}:${normalizedMinutes}`;
};

const convertTimeSlotToMinutes = (value) => {
  const [hours, minutes] = (value || '').split(':').map((part) => Number(part));
  if (Number.isNaN(hours) || Number.isNaN(minutes)) {
    return 0;
  }
  return hours * 60 + minutes;
};

const sortTimeStrings = (a, b) => convertTimeSlotToMinutes(a) - convertTimeSlotToMinutes(b);

const formatTimeSlotLabel = (value) => {
  if (!value) {
    return 'TBD';
  }
  const [hours, minutes] = value.split(':');
  if (hours === undefined || minutes === undefined) {
    return value;
  }
  const snapshot = new Date();
  snapshot.setHours(Number(hours));
  snapshot.setMinutes(Number(minutes));
  snapshot.setSeconds(0);
  return snapshot.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
};

const initialAddSlotState = { open: false, room: null, cell: null, slot: null, slotDisplayTime: '' };
const initialEditSlotState = {
  open: false,
  room: null,
  cell: null,
  slot: null,
  slotDisplayTime: '',
  booking: null,
};

const normalizeSlotLabelsInput = (value) => {
  if (!value) {
    return [];
  }
  if (Array.isArray(value)) {
    return value.map((label) => String(label).trim()).filter(Boolean);
  }
  return String(value)
    .split(',')
    .map((label) => label.trim())
    .filter(Boolean);
};

const getBookingIdentifier = (booking = {}) =>
  booking.bookingId || booking._id || booking.id || booking.referenceId || null;

const normalizePositiveNumber = (value) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) && numeric > 0 ? numeric : null;
};

const sumBookedSlotQuantities = (booking = {}) => {
  if (!Array.isArray(booking.bookedSlots) || !booking.bookedSlots.length) {
    return null;
  }
  const total = booking.bookedSlots.reduce((sum, entry) => {
    const quantity = Number(entry?.quantity);
    return sum + (Number.isFinite(quantity) ? quantity : 0);
  }, 0);
  return total > 0 ? total : null;
};

const derivePartySize = (booking = {}) => {
  const slotQuantityValue = normalizePositiveNumber(booking.slotQuantity);
  const bookedSlotsTotal = sumBookedSlotQuantities(booking);
  const explicitPartySize = normalizePositiveNumber(booking.partySize);
  const legacyQuantity = normalizePositiveNumber(booking.quantity);

  if (bookedSlotsTotal !== null && slotQuantityValue !== null) {
    return Math.max(bookedSlotsTotal, slotQuantityValue);
  }
  if (bookedSlotsTotal !== null) {
    return bookedSlotsTotal;
  }
  if (slotQuantityValue !== null) {
    return slotQuantityValue;
  }
  if (explicitPartySize !== null) {
    return explicitPartySize;
  }
  if (legacyQuantity !== null) {
    return legacyQuantity;
  }
  return 1;
};

const unwrapEmailTemplates = (response) => {
  if (!response) {
    return [];
  }
  if (Array.isArray(response)) {
    return response;
  }
  if (Array.isArray(response.rows)) {
    return response.rows;
  }
  if (Array.isArray(response.data?.rows)) {
    return response.data.rows;
  }
  if (Array.isArray(response.items)) {
    return response.items;
  }
  if (Array.isArray(response.emailTemplates)) {
    return response.emailTemplates;
  }
  if (Array.isArray(response.data)) {
    return response.data;
  }
  return [];
};

const safeString = (value) => (value || '').toString().trim().toLowerCase();

const formatPhoneNumber = (value) => {
  if (!value) {
    return '—';
  }
  const raw = value.toString();
  const digits = raw.replace(/\D+/g, '');
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  if (digits.length === 11 && digits.startsWith('1')) {
    return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  return raw;
};

const deriveBookingCustomerName = (booking = {}) => {
  const customer = booking.customer || {};
  const composed = [customer.firstName, customer.lastName].filter(Boolean).join(' ').trim();
  return booking.customerName || customer.name || customer.fullName || composed || 'Guest';
};

const ScheduleVisualizer = () => {
  const locationId = process.env.REACT_APP_DEFAULT_LOCATION_ID || 'default-location';
  const { rageRoom } = useApi();
  const { emailTemplates: emailTemplatesApi } = useApiBase();
  const rageRoomRef = useRef(rageRoom);

  const [rawRooms, setRawRooms] = useState([]);
  const [roomsLoading, setRoomsLoading] = useState(true);
  const [roomsError, setRoomsError] = useState(null);

  const [availabilityRows, setAvailabilityRows] = useState([]);
  const [availabilityLoading, setAvailabilityLoading] = useState(false);
  const [availabilityError, setAvailabilityError] = useState(null);

  const [cityBookings, setCityBookings] = useState([]);
  const [bookingsLoading, setBookingsLoading] = useState(false);
  const [bookingsError, setBookingsError] = useState(null);

  const [selectedCity, setSelectedCity] = useState('');
  const [selectedDate, setSelectedDate] = useState(DEFAULT_DATE);
  const [refreshTick, setRefreshTick] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [currentPage, setCurrentPage] = useState(1);

  const [addSlotState, setAddSlotState] = useState(initialAddSlotState);
  const [editSlotState, setEditSlotState] = useState(initialEditSlotState);
  const [addSlotSubmitting, setAddSlotSubmitting] = useState(false);
  const [editSlotSubmitting, setEditSlotSubmitting] = useState(false);
  const [addSlotError, setAddSlotError] = useState(null);
  const [editSlotError, setEditSlotError] = useState(null);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendError, setResendError] = useState(null);
  const [resendSuccessMessage, setResendSuccessMessage] = useState('');

  const [emailTemplates, setEmailTemplates] = useState([]);
  const [emailTemplatesLoading, setEmailTemplatesLoading] = useState(true);
  const [emailTemplatesError, setEmailTemplatesError] = useState(null);
  const [selectedEmailTemplateId, setSelectedEmailTemplateId] = useState('');
  const [selectedBookings, setSelectedBookings] = useState(() => new Set());
  const [sendingBookingEmails, setSendingBookingEmails] = useState(false);
  const [bookingSendResult, setBookingSendResult] = useState(null);
  const [exportStatus, setExportStatus] = useState(null);

  const handleRefresh = () => setRefreshTick((prev) => prev + 1);

  useEffect(() => {
    rageRoomRef.current = rageRoom;
  }, [rageRoom]);

  useEffect(() => {
    if (!rageRoomRef.current?.admin?.listRooms) {
      setRoomsLoading(false);
      return;
    }

    let isMounted = true;

    const fetchRooms = async () => {
      setRoomsLoading(true);
      setRoomsError(null);

      try {
        const response = await rageRoomRef.current.admin.listRooms({
          locationId,
          query: { pageSize: 200 },
        });
        if (!isMounted) {
          return;
        }
        setRawRooms(unwrapApiRows(response));
      } catch (error) {
        if (isMounted) {
          setRoomsError(error.message || 'Unable to load rage rooms.');
          setRawRooms([]);
        }
      } finally {
        if (isMounted) {
          setRoomsLoading(false);
        }
      }
    };

    fetchRooms();

    return () => {
      isMounted = false;
    };
  }, [locationId]);

  const preparedRooms = useMemo(() => {
    if (!rawRooms.length) {
      return [];
    }

    return rawRooms
      .map((room) => {
        const roomId = resolveRoomIdFromRoomData(room);
        if (!roomId) {
          return null;
        }

        const city =
          room.city ||
          room.location?.city ||
          room.locationName ||
          'Unassigned City';

        const locationName =
          room.locationName ||
          room.location?.name ||
          room.location?.label ||
          room.location?.title ||
          room.city ||
          'Unknown Location';

        const locationAddress = room.locationAddress || room.location?.address || '';

        const status =
          room.status ||
          room.operationalStatus ||
          (room.isActive === false ? 'Offline' : 'Operational');

        return {
          id: roomId,
          name: room.name || room.displayName || 'Unnamed Room',
          city,
          locationName,
          locationAddress,
          status,
        };
      })
      .filter(Boolean);
  }, [rawRooms]);

  const cityOptions = useMemo(() => {
    const unique = new Set();
    preparedRooms.forEach((room) => {
      if (room.city) {
        unique.add(room.city);
      }
    });
    return Array.from(unique).sort((a, b) => a.localeCompare(b));
  }, [preparedRooms]);

  useEffect(() => {
    if (!selectedCity && cityOptions.length) {
      setSelectedCity(cityOptions[0]);
    }
  }, [cityOptions, selectedCity]);

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedCity, selectedDate, rowsPerPage]);

  const filteredRooms = useMemo(() => {
    if (!selectedCity) {
      return preparedRooms;
    }
    return preparedRooms.filter((room) => room.city === selectedCity);
  }, [preparedRooms, selectedCity]);

  useEffect(() => {
    if (!rageRoomRef.current?.public?.listAvailability || !selectedDate || !selectedCity) {
      setAvailabilityRows([]);
      setAvailabilityLoading(false);
      setAvailabilityError(null);
      return;
    }

    let isMounted = true;

    const fetchAvailability = async () => {
      setAvailabilityLoading(true);
      setAvailabilityError(null);

      try {
        const response = await rageRoomRef.current.public.listAvailability({
          locationId,
          city: selectedCity || undefined,
          startDate: selectedDate,
          endDate: selectedDate,
          onlyAvailable: false,
          pageSize: 500,
        });
        if (!isMounted) {
          return;
        }
        setAvailabilityRows(unwrapApiRows(response));
      } catch (error) {
        if (isMounted) {
          setAvailabilityError(error.message || 'Unable to load schedule availability.');
          setAvailabilityRows([]);
        }
      } finally {
        if (isMounted) {
          setAvailabilityLoading(false);
        }
      }
    };

    fetchAvailability();

    return () => {
      isMounted = false;
    };
  }, [locationId, selectedCity, selectedDate, refreshTick]);

  useEffect(() => {
    if (!rageRoomRef.current?.admin?.listCityBookings || !selectedCity || !selectedDate) {
      setCityBookings([]);
      setBookingsLoading(false);
      setBookingsError(null);
      return;
    }

    let isMounted = true;

    const fetchBookings = async () => {
      setBookingsLoading(true);
      setBookingsError(null);

      try {
        const response = await rageRoomRef.current.admin.listCityBookings({
          locationId,
          query: {
            city: selectedCity,
            date: selectedDate,
            pageNumber: 0,
            pageSize: 1000,
          },
        });
        if (!isMounted) {
          return;
        }
        setCityBookings(unwrapApiRows(response));
      } catch (error) {
        if (isMounted) {
          setBookingsError(error.message || 'Unable to load bookings.');
          setCityBookings([]);
        }
      } finally {
        if (isMounted) {
          setBookingsLoading(false);
        }
      }
    };

    fetchBookings();

    return () => {
      isMounted = false;
    };
  }, [locationId, selectedCity, selectedDate, refreshTick]);

  useEffect(() => {
    let isMounted = true;

    const fetchEmailTemplates = async () => {
      if (!emailTemplatesApi?.search) {
        if (isMounted) {
          setEmailTemplates([]);
          setEmailTemplatesLoading(false);
          setEmailTemplatesError('Email template endpoint is unavailable.');
        }
        return;
      }

      if (isMounted) {
        setEmailTemplatesLoading(true);
        setEmailTemplatesError(null);
      }

      try {
        const response = await emailTemplatesApi.search({
          queryParams: {
            pageOffset: 0,
            pageSize: 50,
          },
        });
        if (!isMounted) {
          return;
        }
        setEmailTemplates(unwrapEmailTemplates(response));
      } catch (fetchError) {
        if (isMounted) {
          setEmailTemplates([]);
          setEmailTemplatesError(fetchError.message || 'Unable to load email templates.');
        }
      } finally {
        if (isMounted) {
          setEmailTemplatesLoading(false);
        }
      }
    };

    fetchEmailTemplates();

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredAvailabilityRows = useMemo(() => {
    if (!filteredRooms.length) {
      return [];
    }
    const roomSet = new Set(filteredRooms.map((room) => room.id));
    return availabilityRows.filter((row) => {
      const rowRoomId = resolveRoomIdFromRow(row);
      return rowRoomId && roomSet.has(rowRoomId);
    });
  }, [availabilityRows, filteredRooms]);

  const availabilityMap = useMemo(() => {
    const map = new Map();

    filteredAvailabilityRows.forEach((row) => {
      const roomId = resolveRoomIdFromRow(row);
      const timeSlot = normalizeTimeSlotValue(row.timeSlot);

      if (!roomId || !timeSlot) {
        return;
      }

      const key = `${roomId}|${timeSlot}`;
      if (!map.has(key)) {
        map.set(key, {
          roomId,
          timeSlot,
          totalCapacity: 0,
          bookedCount: 0,
          remainingCapacity: 0,
          sessionLabels: new Set(),
          slots: [],
          isBlocked: true,
        });
      }

      const entry = map.get(key);
      const slotCapacity = Number(row.maxCapacity) || 0;
      const slotBooked = Number(row.bookedCount) || 0;
      const slotRemaining = Number.isFinite(Number(row.remainingCapacity))
        ? Number(row.remainingCapacity)
        : Math.max(slotCapacity - slotBooked, 0);

      entry.totalCapacity += slotCapacity;
      entry.bookedCount += slotBooked;
      entry.remainingCapacity += Math.max(slotRemaining, 0);
      if (row.sessionLabel) {
        entry.sessionLabels.add(row.sessionLabel);
      }
      entry.isBlocked = entry.isBlocked && row.isBlocked;
      entry.slots.push({
        slotLabel: row.slotLabel,
        maxCapacity: slotCapacity,
        bookedCount: slotBooked,
        remainingCapacity: Math.max(slotRemaining, 0),
        isBlocked: row.isBlocked,
      });
    });

    return map;
  }, [filteredAvailabilityRows]);

  const bookingMap = useMemo(() => {
    const map = new Map();

    cityBookings.forEach((booking) => {
      const roomId = resolveRoomIdFromBooking(booking);
      const timeSlot = normalizeTimeSlotValue(booking.timeSlot);
      if (!roomId || !timeSlot) {
        return;
      }
      const key = `${roomId}|${timeSlot}`;
      const slotQuantityValue = normalizePositiveNumber(booking.slotQuantity);
      const bookedSlots = Array.isArray(booking.bookedSlots) ? booking.bookedSlots : [];
      const normalizedBooking = {
        bookingId: booking.bookingId || booking._id || booking.id,
        roomId,
        timeSlot,
        sessionLabel: booking.sessionLabel || null,
        slotLabels: Array.isArray(booking.slotLabels) ? booking.slotLabels : [],
        slotQuantity: slotQuantityValue,
        bookedSlots,
        customerName: booking.customerName || booking.customer?.name || 'Guest',
        customerEmail: booking.customerEmail || booking.customer?.email || '',
        customerPhoneNumber: booking.customerPhoneNumber || booking.customer?.phoneNumber || '',
        status: booking.status || 'confirmed',
        partySize: derivePartySize(booking),
        specialNote: booking.specialNote || '',
        roomName: booking.roomName || booking.room?.name || '',
        city: booking.city || booking.location?.city || null,
      };
      if (!map.has(key)) {
        map.set(key, []);
      }
      map.get(key).push(normalizedBooking);
    });

    return map;
  }, [cityBookings]);

  const templateOptions = useMemo(() => {
    if (!emailTemplates.length) {
      return [];
    }

    return emailTemplates.map((template, index) => {
      const templateName =
        template.name ||
        template.templateName ||
        template.displayName ||
        template.subject ||
        `template_${index + 1}`;
      const label =
        template.displayName ||
        template.name ||
        template.subject ||
        template.templateName ||
        `Template ${index + 1}`;
      const id = template._id || template.id || template.templateId || `${index}-${label}`;
      return { id, label, templateName };
    });
  }, [emailTemplates]);

  const selectedTemplate = useMemo(() => {
    if (!selectedEmailTemplateId) {
      return null;
    }
    return templateOptions.find((template) => template.id === selectedEmailTemplateId) || null;
  }, [templateOptions, selectedEmailTemplateId]);

  useEffect(() => {
    if (!selectedEmailTemplateId) {
      return;
    }
    const exists = templateOptions.some((template) => template.id === selectedEmailTemplateId);
    if (!exists) {
      setSelectedEmailTemplateId('');
    }
  }, [templateOptions, selectedEmailTemplateId]);

  const templateSelectHelperText = useMemo(() => {
    if (emailTemplatesError) {
      return emailTemplatesError;
    }
    if (emailTemplatesLoading) {
      return 'Loading templates...';
    }
    if (!templateOptions.length) {
      return 'No email templates available.';
    }
    return 'Select an email template to enable email sends.';
  }, [emailTemplatesError, emailTemplatesLoading, templateOptions.length]);

  const bookingTimeSlots = useMemo(() => {
    const slotSet = new Set();
    cityBookings.forEach((booking) => {
      const normalized = normalizeTimeSlotValue(booking.timeSlot);
      if (normalized) {
        slotSet.add(normalized);
      }
    });
    return Array.from(slotSet);
  }, [cityBookings]);

  const timeSlots = useMemo(() => {
    if (!filteredRooms.length) {
      return [];
    }
    const slotSet = new Set();
    filteredAvailabilityRows.forEach((row) => {
      const normalized = normalizeTimeSlotValue(row.timeSlot);
      if (normalized) {
        slotSet.add(normalized);
      }
    });
    bookingTimeSlots.forEach((slot) => slotSet.add(slot));
    if (!slotSet.size) {
      return FALLBACK_TIME_SLOTS;
    }
    return Array.from(slotSet).sort(sortTimeStrings);
  }, [filteredRooms, filteredAvailabilityRows, bookingTimeSlots]);

  const matrixRows = useMemo(() => {
    if (!timeSlots.length || !filteredRooms.length) {
      return [];
    }
    return timeSlots.map((slot) => {
      const cells = filteredRooms.map((room) => {
        const key = `${room.id}|${slot}`;
        const entry = availabilityMap.get(key);
        const bookings = bookingMap.get(key) || [];
        if (!entry && !bookings.length) {
          return null;
        }
        const sessionLabels = entry?.sessionLabels
          ? Array.from(entry.sessionLabels || [])
          : Array.from(
              new Set(bookings.map((booking) => booking.sessionLabel).filter(Boolean))
            );

        if (entry) {
          return {
            ...entry,
            sessionLabels,
            bookings,
          };
        }

        const fallbackBookedCount = bookings.reduce((sum, booking) => {
          const bookingQuantity = Number(booking.partySize);
          return sum + (Number.isFinite(bookingQuantity) ? bookingQuantity : 0);
        }, 0);
        const normalizedBookedCount = fallbackBookedCount || bookings.length;

        return {
          roomId: room.id,
          timeSlot: slot,
          totalCapacity: normalizedBookedCount,
          bookedCount: normalizedBookedCount,
          remainingCapacity: 0,
          sessionLabels,
          slots: [],
          isBlocked: false,
          bookings,
        };
      });
      return {
        slot,
        displayTime: formatTimeSlotLabel(slot),
        cells,
      };
    });
  }, [availabilityMap, bookingMap, filteredRooms, timeSlots]);

  useEffect(() => {
    const maxPage = Math.max(Math.ceil(matrixRows.length / rowsPerPage), 1);
    if (currentPage > maxPage) {
      setCurrentPage(maxPage);
    }
  }, [matrixRows.length, rowsPerPage, currentPage]);

  const paginatedMatrixRows = useMemo(() => {
    if (!matrixRows.length) {
      return [];
    }
    const startIndex = (currentPage - 1) * rowsPerPage;
    return matrixRows.slice(startIndex, startIndex + rowsPerPage);
  }, [matrixRows, currentPage, rowsPerPage]);

  const totalPages = Math.max(Math.ceil(matrixRows.length / rowsPerPage), 1);
  const visibleSlotRangeStart = matrixRows.length ? (currentPage - 1) * rowsPerPage + 1 : 0;
  const visibleSlotRangeEnd = Math.min(currentPage * rowsPerPage, matrixRows.length);

  const hasMatrixData = useMemo(
    () =>
      matrixRows.some((row) =>
        row.cells.some(
          (cell) =>
            cell &&
            (cell.totalCapacity > 0 ||
              cell.slots.length > 0 ||
              (cell.bookings?.length ?? 0) > 0)
        )
      ),
    [matrixRows]
  );

  const normalizedBookings = useMemo(() => {
    if (!cityBookings.length) {
      return [];
    }

    return cityBookings
      .map((booking, index) => {
        const bookingId = getBookingIdentifier(booking) || `${booking.roomId || 'booking'}-${index}`;
        if (!bookingId) {
          return null;
        }
        const normalizedSlot = normalizeTimeSlotValue(booking.timeSlot);
        const customerName = deriveBookingCustomerName(booking);
        const customerEmailRaw = booking.customerEmail || booking.customer?.email || '';
        const customerPhoneRaw = booking.customerPhoneNumber || booking.customer?.phoneNumber || '';
        const customerPhoneFormatted = formatPhoneNumber(customerPhoneRaw);
        const roomName = booking.roomName || booking.room?.name || 'Unnamed Room';
        const city = booking.city || booking.location?.city || booking.room?.city || 'Unknown City';
        const sessionLabel = booking.sessionLabel || '—';
        const partySizeValue = derivePartySize(booking);
        const slotLabels = [
          ...normalizeSlotLabelsInput(booking.slotLabels),
          ...normalizeSlotLabelsInput(booking.slotLabel),
        ];
        const slotLabelsDisplay = slotLabels.length ? slotLabels.join(', ') : '—';
        const status = booking.status || 'confirmed';
        const specialNote = booking.specialNote || '';
        const bookingDate = booking.date || selectedDate;

        return {
          bookingId,
          city,
          roomName,
          sessionLabel,
          timeSlot: normalizedSlot,
          timeSlotDisplay: formatTimeSlotLabel(normalizedSlot),
          customerName,
          customerEmail: customerEmailRaw || '—',
          customerEmailRaw,
          customerPhone: customerPhoneFormatted,
          customerPhoneRaw,
          partySize: Number.isFinite(partySizeValue) ? partySizeValue : null,
          slotLabels,
          slotLabelsDisplay,
          status,
          specialNote,
          date: bookingDate,
        };
      })
      .filter(Boolean)
      .sort((a, b) => {
        const cityDiff = safeString(a.city).localeCompare(safeString(b.city));
        if (cityDiff !== 0) {
          return cityDiff;
        }
        const roomDiff = safeString(a.roomName).localeCompare(safeString(b.roomName));
        if (roomDiff !== 0) {
          return roomDiff;
        }
        return convertTimeSlotToMinutes(a.timeSlot || '') - convertTimeSlotToMinutes(b.timeSlot || '');
      });
  }, [cityBookings, selectedDate]);

  useEffect(() => {
    setSelectedBookings((prev) => {
      if (!prev.size) {
        return prev;
      }
      const validIds = new Set(normalizedBookings.map((booking) => booking.bookingId));
      let changed = false;
      const next = new Set();
      prev.forEach((id) => {
        if (validIds.has(id)) {
          next.add(id);
        } else {
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [normalizedBookings]);

  useEffect(() => {
    setSelectedBookings(new Set());
    setBookingSendResult(null);
  }, [selectedCity, selectedDate]);

  useEffect(() => {
    setExportStatus(null);
  }, [selectedCity, selectedDate]);

  const scheduleExportRows = useMemo(() => {
    if (!matrixRows.length || !filteredRooms.length) {
      return [];
    }

    return matrixRows
      .flatMap((row) =>
        (row.cells || []).map((cell, index) => {
          const room = filteredRooms[index];
          if (!room) {
            return null;
          }
          const slotLabels = cell?.slots
            ? cell.slots
                .map((slot, slotIndex) => slot?.slotLabel || `Slot ${slotIndex + 1}`)
                .filter(Boolean)
                .join('; ')
            : '';
          const bookings = cell?.bookings || [];
          const bookingCustomerNames = bookings
            .map((booking) => booking.customerName || 'Guest')
            .join('; ');
          const bookingCustomerEmails = bookings
            .map((booking) => booking.customerEmail)
            .filter(Boolean)
            .join('; ');
          const bookingCustomerPhones = bookings
            .map((booking) => booking.customerPhoneNumber)
            .filter(Boolean)
            .join('; ');
          const bookingStatuses = bookings
            .map((booking) => booking.status || 'confirmed')
            .join('; ');
          const remainingCapacityValue = cell
            ? getEffectiveRemainingCapacity(cell)
            : null;

          return {
            city: room.city || 'Unassigned City',
            date: selectedDate,
            roomName: room.name,
            roomStatus: room.status,
            timeSlot: row.slot,
            timeSlotDisplay: row.displayTime,
            sessionLabels: cell?.sessionLabels?.join(' | ') || '',
            totalCapacity: cell?.totalCapacity ?? 0,
            bookedCount: cell?.bookedCount ?? 0,
            remainingCapacity:
              typeof remainingCapacityValue === 'number' ? remainingCapacityValue : '',
            isBlocked: cell ? (cell.isBlocked ? 'Yes' : 'No') : 'No schedule',
            slotCount: cell?.slots?.length ?? 0,
            slotLabels,
            bookingCount: bookings.length,
            bookingCustomerNames,
            bookingCustomerEmails,
            bookingCustomerPhones,
            bookingStatuses,
            notes: !cell ? 'No schedule configured' : cell.isBlocked ? 'Slot blocked' : '',
          };
        })
      )
      .filter(Boolean);
  }, [filteredRooms, matrixRows, selectedDate]);

  const bookingExportRows = useMemo(() => {
    if (!normalizedBookings.length) {
      return [];
    }

    return normalizedBookings.map((booking) => ({
      bookingId: booking.bookingId,
      date: booking.date,
      city: booking.city,
      roomName: booking.roomName,
      sessionLabel: booking.sessionLabel,
      timeSlot: booking.timeSlot,
      timeSlotDisplay: booking.timeSlotDisplay,
      customerName: booking.customerName,
      customerEmail: booking.customerEmailRaw || booking.customerEmail || '',
      customerPhone: booking.customerPhoneRaw || booking.customerPhone || '',
      partySize: booking.partySize ?? '',
      slotLabels: booking.slotLabels?.length ? booking.slotLabels.join(', ') : '',
      status: booking.status,
      specialNote: booking.specialNote || '',
    }));
  }, [normalizedBookings]);

  const templateSelectDisabled = emailTemplatesLoading || !templateOptions.length;
  const hasBookings = normalizedBookings.length > 0;
  const selectedBookingCount = selectedBookings.size;
  const allBookingsSelected = hasBookings && selectedBookingCount === normalizedBookings.length;
  const isBookingSelectIndeterminate =
    selectedBookingCount > 0 && selectedBookingCount < normalizedBookings.length;
  const sendBookingButtonLabel = sendingBookingEmails
    ? 'Sending emails...'
    : !selectedTemplate
      ? 'Select template to send'
      : selectedBookingCount > 1
        ? `Send ${selectedBookingCount} booking emails`
        : 'Send booking email';
  const canSendBookingEmails =
    selectedBookingCount > 0 && !sendingBookingEmails && Boolean(selectedTemplate);
  const disableScheduleExport = availabilityLoading || bookingsLoading || !scheduleExportRows.length;
  const disableBookingsExport = bookingsLoading || !bookingExportRows.length;

  const openAddSlotModal = ({ room, cell, slot }) => {
    const resolvedSlot = slot || cell?.timeSlot || null;
    setAddSlotState({
      open: true,
      room: room || null,
      cell: cell || null,
      slot: resolvedSlot,
      slotDisplayTime: resolvedSlot ? formatTimeSlotLabel(resolvedSlot) : 'TBD',
    });
    setAddSlotError(null);
  };

  const closeAddSlotModal = () => {
    setAddSlotState(initialAddSlotState);
    setAddSlotError(null);
  };

  const openEditSlotModal = ({ room, cell, slot, booking }) => {
    if (!booking) {
      return;
    }
    const resolvedSlot = slot || cell?.timeSlot || booking.timeSlot || null;
    setEditSlotState({
      open: true,
      room: room || null,
      cell: cell || null,
      slot: resolvedSlot,
      slotDisplayTime: resolvedSlot ? formatTimeSlotLabel(resolvedSlot) : 'TBD',
      booking,
    });
    setEditSlotError(null);
    setResendError(null);
    setResendSuccessMessage('');
  };

  const closeEditSlotModal = () => {
    setEditSlotState(initialEditSlotState);
    setEditSlotError(null);
    setResendError(null);
    setResendSuccessMessage('');
  };

  const buildBookingPayload = ({ formValues, room, cell, slotValue, fallbackBooking }) => {
    const slotLabels = normalizeSlotLabelsInput(formValues.slotLabels);
    const derivedBooking = fallbackBooking || {};
    return {
      locationId,
      roomId: room?.id || derivedBooking.roomId || derivedBooking.room?.id || null,
      date: selectedDate,
      timeSlot: slotValue || derivedBooking.timeSlot || null,
      sessionLabel:
        derivedBooking.sessionLabel ||
        (Array.isArray(cell?.sessionLabels) && cell.sessionLabels.length
          ? cell.sessionLabels[0]
          : undefined),
      slotLabels,
      slotQuantity:
        Number.isFinite(Number(formValues.partySize)) && Number(formValues.partySize) > 0
          ? Number(formValues.partySize)
          : derivePartySize(derivedBooking),
      customer: {
        name: formValues.customerName?.trim() || derivedBooking.customerName || 'Guest',
        email: formValues.customerEmail?.trim() || derivedBooking.customerEmail || '',
        phoneNumber:
          formValues.customerPhoneNumber?.trim() ||
          derivedBooking.customerPhoneNumber ||
          '',
      },
      specialNote: formValues.specialNote?.trim() || derivedBooking.specialNote || '',
    };
  };

  const handleAddSlotSubmit = async (formValues) => {
    if (!rageRoomRef.current?.admin?.createBooking) {
      setAddSlotError('Admin booking API is unavailable.');
      return;
    }
    const payload = buildBookingPayload({
      formValues,
      room: addSlotState.room,
      cell: addSlotState.cell,
      slotValue: addSlotState.slot,
    });
    if (!payload.roomId || !payload.timeSlot) {
      setAddSlotError('Unable to determine room or time slot for this booking.');
      return;
    }
    setAddSlotSubmitting(true);
    setAddSlotError(null);
    try {
      await rageRoomRef.current.admin.createBooking({ payload });
      closeAddSlotModal();
      handleRefresh();
    } catch (error) {
      setAddSlotError(error.message || 'Unable to create booking.');
    } finally {
      setAddSlotSubmitting(false);
    }
  };

  const handleEditSlotSubmit = async (formValues) => {
    if (!rageRoomRef.current?.admin?.updateBooking) {
      setEditSlotError('Admin booking update API is unavailable.');
      return;
    }
    const bookingId = getBookingIdentifier(editSlotState.booking);
    if (!bookingId) {
      setEditSlotError('Missing booking identifier.');
      return;
    }
    const payload = buildBookingPayload({
      formValues,
      room: editSlotState.room,
      cell: editSlotState.cell,
      slotValue: editSlotState.slot,
      fallbackBooking: editSlotState.booking,
    });
    if (!payload.roomId || !payload.timeSlot) {
      setEditSlotError('Unable to determine room or time slot for this booking.');
      return;
    }
    setEditSlotSubmitting(true);
    setEditSlotError(null);
    try {
      await rageRoomRef.current.admin.updateBooking({ bookingId, payload });
      closeEditSlotModal();
      handleRefresh();
    } catch (error) {
      setEditSlotError(error.message || 'Unable to update booking.');
    } finally {
      setEditSlotSubmitting(false);
    }
  };

  const handleResendBookingConfirmation = async () => {
    if (!rageRoomRef.current?.admin?.resendBookingConfirmationEmail) {
      setResendError('Resend confirmation API is unavailable.');
      return;
    }
    const bookingId = getBookingIdentifier(editSlotState.booking);
    if (!bookingId) {
      setResendError('Missing booking identifier.');
      return;
    }
    setResendLoading(true);
    setResendError(null);
    setResendSuccessMessage('');
    try {
      await rageRoomRef.current.admin.resendBookingConfirmationEmail({ bookingId });
      setResendSuccessMessage('Confirmation email resent successfully.');
    } catch (error) {
      setResendError(error.message || 'Unable to resend confirmation email.');
    } finally {
      setResendLoading(false);
    }
  };

  const handleCityChange = (event) => setSelectedCity(event.target.value);
  const handleDateChange = (event) => setSelectedDate(event.target.value);
  const handleRowsPerPageChange = (event) => setRowsPerPage(Number(event.target.value));
  const handlePageChange = (_event, value) => setCurrentPage(value);

  const handleEmailTemplateChange = (event) => setSelectedEmailTemplateId(event.target.value);

  const toggleBookingSelection = (bookingId) => {
    setSelectedBookings((prev) => {
      const next = new Set(prev);
      if (next.has(bookingId)) {
        next.delete(bookingId);
      } else {
        next.add(bookingId);
      }
      return next;
    });
  };

  const handleSelectAllBookings = (event) => {
    const { checked } = event.target;
    if (checked) {
      setSelectedBookings(new Set(normalizedBookings.map((booking) => booking.bookingId)));
    } else {
      setSelectedBookings(new Set());
    }
  };

  const handleSendBookingEmails = async () => {
    if (!selectedBookings.size || sendingBookingEmails) {
      return;
    }
    if (!selectedTemplate) {
      setBookingSendResult({
        severity: 'error',
        summary: 'Select an email template before sending booking emails.',
        errors: [],
      });
      return;
    }

    const bookingEmailApi =
      rageRoomRef.current?.admin?.sendBookingReminderEmail;

    if (!bookingEmailApi) {
      setBookingSendResult({
        severity: 'error',
        summary: 'Booking email endpoint is unavailable. Please contact an administrator.',
        errors: [],
      });
      return;
    }

    setSendingBookingEmails(true);
    setBookingSendResult(null);

    const bookingIds = Array.from(selectedBookings);
    const errors = [];

    for (const bookingId of bookingIds) {
      try {
        await bookingEmailApi({
          bookingId,
          templateName: selectedTemplate.templateName || selectedTemplate.label,
        });
      } catch (sendError) {
        const relatedBooking = normalizedBookings.find((booking) => booking.bookingId === bookingId);
        errors.push({
          id: bookingId,
          customerName: relatedBooking?.customerName || 'Guest',
          message: sendError?.message || 'Unable to send booking email.',
        });
      }
    }

    setSendingBookingEmails(false);
    setSelectedBookings(() => {
      if (!errors.length) {
        return new Set();
      }
      const failedIds = new Set(errors.map((error) => error.id));
      const next = new Set();
      bookingIds.forEach((id) => {
        if (failedIds.has(id)) {
          next.add(id);
        }
      });
      return next;
    });

    setBookingSendResult({
      severity: errors.length ? 'warning' : 'success',
      summary: errors.length
        ? `Sent ${bookingIds.length - errors.length} email(s); ${errors.length} failed.`
        : `Successfully sent ${bookingIds.length} booking email${bookingIds.length === 1 ? '' : 's'}.`,
      errors,
    });
  };

  const buildExportFilename = (prefix) => {
    const citySegment = slugifyForFilename(selectedCity || 'all-cities');
    const dateSegment = sanitizeDateForFilename(selectedDate || 'date');
    return `${prefix}_${citySegment}_${dateSegment}.csv`;
  };

  const handleExportScheduleCsv = () => {
    if (!scheduleExportRows.length) {
      setExportStatus({
        severity: 'info',
        message: 'No schedule data available to export for the selected filters.',
      });
      return;
    }

    const csvContent = buildCsvContent({
      columns: SCHEDULE_CSV_COLUMNS,
      rows: scheduleExportRows,
    });

    downloadCsvFile({
      filename: buildExportFilename('rage-room-schedule'),
      csvContent,
    });

    setExportStatus({
      severity: 'success',
      message: `Schedule CSV downloaded (${scheduleExportRows.length} row${
        scheduleExportRows.length === 1 ? '' : 's'
      }).`,
    });
  };

  const handleExportBookingsCsv = () => {
    if (!bookingExportRows.length) {
      setExportStatus({
        severity: 'info',
        message: 'No booking data available to export for the selected filters.',
      });
      return;
    }

    const csvContent = buildCsvContent({
      columns: BOOKING_CSV_COLUMNS,
      rows: bookingExportRows,
    });

    downloadCsvFile({
      filename: buildExportFilename('rage-room-bookings'),
      csvContent,
    });

    setExportStatus({
      severity: 'success',
      message: `Booking CSV downloaded (${bookingExportRows.length} row${
        bookingExportRows.length === 1 ? '' : 's'
      }).`,
    });
  };

  const combinedLoading = availabilityLoading || bookingsLoading;

  return (
    <Fragment>
      <Stack spacing={4}>
        <Stack spacing={1}>
          <Typography variant="h4">Visual Smash Schedule</Typography>
          <Typography color="text.secondary">
            Compare rage room utilization by city and date. Each dot represents a single smasher in a
            30-minute session.
          </Typography>
        </Stack>

        <FiltersBar
          cityOptions={cityOptions}
          selectedCity={selectedCity}
          onCityChange={handleCityChange}
          selectedDate={selectedDate}
          onDateChange={handleDateChange}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleRowsPerPageChange}
          rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
          onRefresh={handleRefresh}
          loading={combinedLoading}
        />

        <ScheduleLegend />

        <Stack spacing={1.5}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1.5}
            alignItems={{ xs: 'stretch', sm: 'center' }}
          >
            <Button
              variant="outlined"
              color="primary"
              onClick={handleExportScheduleCsv}
              disabled={disableScheduleExport}
            >
              Export schedule CSV
            </Button>
            <Button
              variant="outlined"
              color="secondary"
              onClick={handleExportBookingsCsv}
              disabled={disableBookingsExport}
            >
              Export bookings CSV
            </Button>
          </Stack>
          {exportStatus && (
            <Alert severity={exportStatus.severity} onClose={() => setExportStatus(null)}>
              {exportStatus.message}
            </Alert>
          )}
        </Stack>

        {roomsError && <Alert severity="error">{roomsError}</Alert>}
        {availabilityError && <Alert severity="error">{availabilityError}</Alert>}
        {bookingsError && <Alert severity="error">{bookingsError}</Alert>}

        {roomsLoading ? (
          <Paper variant="outlined" sx={{ p: 3 }}>
            <Stack spacing={2}>
              <Typography variant="body1">Loading rooms…</Typography>
              <LinearProgress />
            </Stack>
          </Paper>
        ) : !filteredRooms.length ? (
          <Alert severity="info">No rooms match the selected city filter.</Alert>
        ) : (
          <ScheduleMatrix
            filteredRooms={filteredRooms}
            paginatedMatrixRows={paginatedMatrixRows}
            matrixRowsLength={matrixRows.length}
            rowsPerPage={rowsPerPage}
            currentPage={currentPage}
            totalPages={totalPages}
            visibleSlotRangeStart={visibleSlotRangeStart}
            visibleSlotRangeEnd={visibleSlotRangeEnd}
            combinedLoading={combinedLoading}
            onPageChange={handlePageChange}
            hasMatrixData={hasMatrixData}
            onAddSlot={openAddSlotModal}
            onEditBooking={openEditSlotModal}
          />
        )}

        <Stack spacing={2}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={3}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', md: 'center' }}
          >
            <Box>
              <Typography variant="h5">Booked Guest Outreach</Typography>
              <Typography color="text.secondary">
                Select bookings from the list to send a templated email to confirmed guests.
              </Typography>
            </Box>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1.5}
              alignItems={{ xs: 'stretch', sm: 'center' }}
              justifyContent="flex-end"
              sx={{ width: { xs: '100%', sm: 'auto' } }}
            >
              <FormControl
                size="small"
                sx={{ minWidth: 240 }}
                error={Boolean(emailTemplatesError)}
                disabled={templateSelectDisabled}
                required
              >
                <InputLabel id="booking-email-template-label">Email template</InputLabel>
                <Select
                  labelId="booking-email-template-label"
                  value={selectedEmailTemplateId}
                  label="Email template"
                  onChange={handleEmailTemplateChange}
                >
                  <MenuItem value="">
                    <em>Select template</em>
                  </MenuItem>
                  {templateOptions.map((template) => (
                    <MenuItem key={template.id} value={template.id}>
                      {template.label}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>{templateSelectHelperText}</FormHelperText>
              </FormControl>

              <FormControl
                size="small"
                sx={{ minWidth: 240 }}
                disabled={templateSelectDisabled}
              >
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={handleSendBookingEmails}
                  disabled={!canSendBookingEmails}
                >
                  {sendBookingButtonLabel}
                </Button>
                <FormHelperText>
                  Send the selected template to every highlighted booking.
                </FormHelperText>
              </FormControl>

              {selectedBookingCount > 0 && (
                <Typography variant="caption" color="text.secondary">
                  {selectedBookingCount} selected
                </Typography>
              )}
            </Stack>
          </Stack>

          {bookingSendResult && (
            <Alert severity={bookingSendResult.severity} onClose={() => setBookingSendResult(null)}>
              <Typography variant="body2">{bookingSendResult.summary}</Typography>
              {bookingSendResult.errors?.length > 0 && (
                <Box component="ul" sx={{ pl: 2, mb: 0 }}>
                  {bookingSendResult.errors.map((error) => (
                    <li key={error.id}>
                      <Typography variant="caption">
                        {error.customerName}: {error.message}
                      </Typography>
                    </li>
                  ))}
                </Box>
              )}
            </Alert>
          )}

          <Paper variant="outlined">
            {bookingsLoading && <LinearProgress color="secondary" />}
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={allBookingsSelected}
                        indeterminate={isBookingSelectIndeterminate}
                        onChange={handleSelectAllBookings}
                        inputProps={{ 'aria-label': 'select all bookings' }}
                      />
                    </TableCell>
                    <TableCell>City</TableCell>
                    <TableCell>Room</TableCell>
                    <TableCell>Session</TableCell>
                    <TableCell>Time Slot</TableCell>
                    <TableCell>Customer</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Phone</TableCell>
                    <TableCell align="right">Party Size</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {hasBookings ? (
                    normalizedBookings.map((booking) => (
                      <TableRow
                        key={booking.bookingId}
                        hover
                        selected={selectedBookings.has(booking.bookingId)}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={selectedBookings.has(booking.bookingId)}
                            onChange={() => toggleBookingSelection(booking.bookingId)}
                            inputProps={{
                              'aria-label': `select booking for ${booking.customerName}`,
                            }}
                          />
                        </TableCell>
                        <TableCell>{booking.city}</TableCell>
                        <TableCell>{booking.roomName}</TableCell>
                        <TableCell>{booking.sessionLabel}</TableCell>
                        <TableCell>{booking.timeSlotDisplay}</TableCell>
                        <TableCell>{booking.customerName}</TableCell>
                        <TableCell>{booking.customerEmail}</TableCell>
                        <TableCell>{booking.customerPhone}</TableCell>
                        <TableCell align="right">{booking.partySize ?? '—'}</TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary">
                          {bookingsLoading
                            ? 'Fetching bookings...'
                            : 'No bookings found for the selected filters.'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Stack>
      </Stack>

      <AddSlotModal
        open={addSlotState.open}
        onClose={closeAddSlotModal}
        room={addSlotState.room}
        cell={addSlotState.cell}
        slotDisplayTime={addSlotState.slotDisplayTime}
        timeSlotValue={addSlotState.slot}
        date={selectedDate}
        onSubmit={handleAddSlotSubmit}
        isSubmitting={addSlotSubmitting}
        error={addSlotError}
        slotLabelSuggestions={
          addSlotState.cell?.slots
            ? addSlotState.cell.slots
                .map((slot) => slot.slotLabel)
                .filter((label) => !!label)
            : []
        }
      />

      <EditSlotModal
        open={editSlotState.open}
        onClose={closeEditSlotModal}
        room={editSlotState.room}
        cell={editSlotState.cell}
        booking={editSlotState.booking}
        slotDisplayTime={editSlotState.slotDisplayTime}
        timeSlotValue={editSlotState.slot}
        date={selectedDate}
        onSubmit={handleEditSlotSubmit}
        isSubmitting={editSlotSubmitting}
        error={editSlotError}
        slotLabelSuggestions={
          editSlotState.cell?.slots
            ? editSlotState.cell.slots
                .map((slot) => slot.slotLabel)
                .filter((label) => !!label)
            : []
        }
        onResendEmail={handleResendBookingConfirmation}
        isResendingEmail={resendLoading}
        resendError={resendError}
        resendSuccessMessage={resendSuccessMessage}
      />
    </Fragment>
  );
};

export default ScheduleVisualizer;